<template>
  <div class="report-field" :class="{ 'report-field--compact': compact }">
    <!-- Texto / número / dinero / fecha: label fijo arriba, nunca flotante, para que las cajas de una
         misma fila queden a la misma altura sin importar cuáles ya tienen valor. -->
    <div v-if="isTextLike" class="report-field__text">
      <div v-if="!hideLabel" class="report-field__label">{{ labelText }}</div>
      <v-text-field
        :value="value"
        :aria-label="field.label"
        :type="inputType"
        :prefix="isCurrencyField ? '$' : undefined"
        :hint="field.type === 'MONEY' && value ? (isCurrencyField ? formatMoney(value) : formatNumber(value)) : field.help"
        :persistent-hint="field.type === 'MONEY' && !!value"
        :min="isNumeric ? 0 : undefined"
        :readonly="readonly"
        outlined
        dense
        hide-details="auto"
        @input="emitScalar"
      />
    </div>

    <v-textarea
      v-else-if="field.type === 'TEXTAREA'"
      :value="value"
      :label="labelText"
      :aria-label="field.label"
      :placeholder="hideLabel ? 'Escribe aquí…' : undefined"
      :hint="field.help"
      :rows="textareaRows"
      :counter="readonly ? undefined : 6000"
      maxlength="6000"
      :readonly="readonly"
      auto-grow
      outlined
      dense
      @input="emitScalar"
    />

    <div v-else-if="field.type === 'SELECT'" class="report-field__text">
      <div v-if="!hideLabel" class="report-field__label">{{ labelText }}</div>
      <v-select
        :value="value"
        :items="field.options"
        item-text="label"
        item-value="value"
        :aria-label="field.label"
        :placeholder="readonly ? '—' : 'Seleccionar'"
        :clearable="!readonly"
        :readonly="readonly"
        :menu-props="readonly ? { disabled: true } : undefined"
        outlined
        dense
        hide-details="auto"
        @change="v => $emit('input', v === undefined ? null : v)"
      />
    </div>

    <!-- Opciones: pregunta a la izquierda, opciones a la derecha (como en el PDF) -->
    <div
      v-else-if="field.type === 'CHOICE' || field.type === 'MULTI_CHOICE'"
      class="report-field__choice"
      :class="{ 'report-field__choice--readonly': readonly }"
      role="group"
      :aria-label="field.label"
      :aria-readonly="readonly ? 'true' : null"
    >
      <span v-if="!hideLabel" class="report-field__question">{{ labelText }}</span>
      <v-btn-toggle
        :value="value"
        :multiple="field.type === 'MULTI_CHOICE'"
        active-class="report-field__option--active"
        class="report-field__options"
        dense
        @change="onChoice"
      >
        <!-- Read only keeps the selected option with its own color (as in the PDF) instead of greying it out. -->
        <v-btn
          v-for="option in field.options"
          :key="option.value"
          :value="option.value"
          :tabindex="readonly ? -1 : undefined"
          small
          class="report-field__option"
          :class="yesNoOptionClass(option)"
        >{{ option.label }}</v-btn>
      </v-btn-toggle>
    </div>

    <!-- Foto -->
    <div v-else-if="field.type === 'PHOTO'" class="report-field__photo">
      <div class="report-field__label">{{ labelText }}</div>
      <div class="report-field__photo-frame">
        <v-img v-if="value && value.url" :src="value.url" :alt="field.label" contain height="100%" />
        <div v-else-if="readonly" class="report-field__photo-empty">
          <v-icon color="grey">mdi-camera-off-outline</v-icon>
          <span>Sin foto</span>
        </div>
        <button
          v-else
          type="button"
          class="report-field__photo-empty"
          :disabled="uploading"
          :aria-label="`Cargar ${field.label.toLowerCase()}`"
          @click="$refs.picker.click()"
        >
          <v-progress-circular v-if="uploading" indeterminate size="20" width="2" color="grey" />
          <v-icon v-else color="grey">mdi-camera-plus-outline</v-icon>
          <span>{{ uploading ? 'Cargando…' : 'Cargar foto' }}</span>
        </button>
      </div>
      <div v-if="value && !readonly" class="d-flex">
        <v-btn x-small text color="primary" :loading="uploading" @click="$refs.picker.click()">
          <v-icon left x-small>mdi-camera</v-icon>Cambiar
        </v-btn>
        <v-btn x-small text color="grey darken-1" @click="$emit('input', null)">Quitar</v-btn>
      </div>
      <input v-if="!readonly" ref="picker" type="file" accept="image/*" class="d-none" @change="onPhoto">
    </div>

    <!-- Anexos -->
    <div v-else-if="field.type === 'FILES'">
      <div class="d-flex flex-wrap align-center mb-2">
        <div class="mr-4">
          <div class="report-field__label mb-0">{{ labelText }}</div>
          <div v-if="field.help" class="caption grey--text">{{ field.help }}</div>
        </div>
        <v-spacer />
        <v-btn
          v-if="!readonly"
          small
          outlined
          color="primary"
          :loading="uploading"
          :disabled="files.length >= (field.maxFiles || 30)"
          @click="$refs.filesPicker.click()"
        >
          <v-icon left small>mdi-paperclip</v-icon>Adjuntar soportes
        </v-btn>
      </div>
      <div v-if="files.length" class="report-field__files">
        <div v-for="(file, index) in files" :key="file.path" class="report-field__file">
          <v-icon small :color="file.contentType === 'application/pdf' ? 'red darken-1' : 'primary'">
            {{ file.contentType === 'application/pdf' ? 'mdi-file-pdf-box' : 'mdi-file-image-outline' }}
          </v-icon>
          <a :href="file.url" target="_blank" rel="noopener" class="report-field__file-name">{{ file.name }}</a>
          <v-btn v-if="!readonly" icon x-small :aria-label="`Quitar ${file.name}`" @click="removeFile(index)">
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
      <div v-else class="report-field__empty">Aún no hay soportes adjuntos.</div>
      <input
        v-if="!readonly"
        ref="filesPicker"
        type="file"
        accept="image/*,application/pdf"
        multiple
        class="d-none"
        @change="onFiles"
      >
    </div>

    <!-- Tabla de filas fijas -->
    <div v-else-if="field.type === 'TABLE'" class="report-field__table">
      <div v-if="!readonly" class="d-flex justify-end mb-1">
        <v-btn x-small text color="primary" @click="fillTable">
          <v-icon left x-small>mdi-check-all</v-icon>Marcar todas: {{ tableDefaultsLabel }}
        </v-btn>
      </div>
      <v-simple-table dense>
        <thead>
          <tr>
            <th>{{ field.label }}</th>
            <th v-for="column in field.fields" :key="column.key">{{ column.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in field.rows" :key="row.value">
            <td class="report-field__table-label">{{ row.label }}</td>
            <td v-for="column in field.fields" :key="column.key" class="report-field__table-cell">
              <report-field
                :field="column"
                :value="cell(row.value, column.key)"
                :storage-folder="storageFolder"
                :readonly="readonly"
                hide-label
                compact
                @input="v => setCell(row.value, column.key, v)"
              />
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>

    <!-- Lista repetible -->
    <div v-else-if="field.type === 'LIST'" class="report-field__list">
      <div class="report-field__label">{{ field.label }}</div>
      <div v-for="(item, index) in items" :key="index" class="report-field__item">
        <div class="report-field__item-head">
          <span>{{ field.itemLabel }} {{ index + 1 }}</span>
          <v-btn
            v-if="!readonly"
            icon
            x-small
            :aria-label="`Quitar ${field.itemLabel.toLowerCase()} ${index + 1}`"
            @click="removeItem(index)"
          >
            <v-icon small>mdi-delete-outline</v-icon>
          </v-btn>
        </div>
        <v-row dense>
          <v-col v-for="sub in field.fields" :key="sub.key" v-bind="colProps(sub)">
            <report-field
              :field="sub"
              :value="item[sub.key]"
              :storage-folder="storageFolder"
              :readonly="readonly"
              compact
              @input="v => setItem(index, sub.key, v)"
            />
          </v-col>
        </v-row>
      </div>
      <div v-if="readonly && !items.length" class="report-field__empty">Sin registros.</div>
      <div class="d-flex align-center flex-wrap">
        <v-btn
          v-if="!readonly"
          small
          text
          color="primary"
          :disabled="items.length >= (field.maxItems || 20)"
          @click="addItem"
        >
          <v-icon left small>mdi-plus</v-icon>Agregar {{ field.itemLabel.toLowerCase() }}
        </v-btn>
        <v-spacer />
        <span v-if="moneyField" class="report-field__total">
          Total {{ field.itemLabel.toLowerCase() }}s: <strong>{{ formattedListTotal }}</strong>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { uploadFile } from '../../services'
import { compressImage, isImage, isPdf } from '../../utils/images'
import { fieldCols } from './layout'

export default {
  name: 'ReportField',
  props: {
    field: { type: Object, required: true },
    value: { type: [String, Number, Array, Object], default: null },
    storageFolder: { type: String, required: true },
    hideLabel: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
    // Sections of other analysts and reports of closed processes are shown without any way to change them.
    readonly: { type: Boolean, default: false }
  },
  data: () => ({ uploading: false }),
  computed: {
    isNumeric () { return ['NUMBER', 'MONEY'].includes(this.field.type) },
    isCurrencyField () { return this.field.type === 'MONEY' && !this.field.hideCurrencySymbol },
    textareaRows () {
      const hasValue = this.value !== null && this.value !== undefined && this.value !== ''
      if (this.compact) return hasValue ? 2 : 1
      return hasValue ? 4 : 2
    },
    isYesNo () {
      const options = this.field.options || []
      return this.field.type === 'CHOICE' && options.length === 2 &&
        options.every(o => ['YES', 'NO'].includes(o.value))
    },
    isTextLike () { return ['TEXT', 'NUMBER', 'MONEY', 'DATE'].includes(this.field.type) },
    inputType () {
      if (this.isNumeric) return 'number'
      if (this.field.type === 'DATE') return 'date'
      return 'text'
    },
    labelText () {
      if (this.hideLabel) return ''
      return this.field.required ? `${this.field.label} *` : this.field.label
    },
    files () { return Array.isArray(this.value) ? this.value : [] },
    items () { return Array.isArray(this.value) ? this.value : [] },
    moneyField () { return (this.field.fields || []).find(f => f.type === 'MONEY') || null },
    listTotal () {
      if (!this.moneyField) return 0
      return this.items.reduce((sum, item) => sum + (Number(item[this.moneyField.key]) || 0), 0)
    },
    tableDefaultsLabel () {
      return (this.field.fields || []).map(c => c.options && c.options[0] && c.options[0].label).filter(Boolean).join(' / ')
    },
    formattedListTotal () {
      if (this.moneyField && this.moneyField.hideCurrencySymbol) return this.formatNumber(this.listTotal)
      return this.formatMoney(this.listTotal)
    }
  },
  methods: {
    colProps (field) {
      // Half-width lists (income / expenses) are narrow: type and amount share one line.
      if (this.field.width === 'half') return { cols: field.type === 'MONEY' ? 5 : 7 }
      return fieldCols(field)
    },
    formatNumber (value) {
      return Math.round(Number(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    formatMoney (value) {
      return `$ ${this.formatNumber(value)}`
    },
    emitScalar (value) {
      if (this.isNumeric) {
        this.$emit('input', value === '' || value === null ? null : Number(value))
        return
      }
      this.$emit('input', value === '' ? null : value)
    },
    yesNoOptionClass (option) {
      if (!this.isYesNo) return null
      return option.value === 'YES' ? 'report-field__option--yes' : 'report-field__option--no'
    },
    onChoice (value) {
      if (this.readonly) return
      if (this.field.type === 'MULTI_CHOICE') this.$emit('input', value || [])
      else this.$emit('input', value === undefined ? null : value)
    },
    cell (row, column) {
      return (this.value && this.value[row] && this.value[row][column]) ?? null
    },
    setCell (row, column, cellValue) {
      const table = { ...(this.value || {}) }
      table[row] = { ...(table[row] || {}), [column]: cellValue }
      this.$emit('input', table)
    },
    fillTable () {
      const table = {}
      ;(this.field.rows || []).forEach(row => {
        table[row.value] = {}
        ;(this.field.fields || []).forEach(column => {
          table[row.value][column.key] = column.options && column.options[0] ? column.options[0].value : null
        })
      })
      this.$emit('input', table)
    },
    addItem () { this.$emit('input', [...this.items, {}]) },
    removeItem (index) { this.$emit('input', this.items.filter((_, i) => i !== index)) },
    setItem (index, key, itemValue) {
      this.$emit('input', this.items.map((item, i) => (i === index ? { ...item, [key]: itemValue } : item)))
    },
    async upload (file) {
      const stamp = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
      if (isPdf(file)) {
        const name = `${this.field.key}-${stamp}.pdf`
        const url = await uploadFile(this.storageFolder, name, file)
        return { path: `${this.storageFolder}/${name}`, url, name: file.name, contentType: 'application/pdf' }
      }
      if (!isImage(file)) throw new Error(`"${file.name}" no es una imagen ni un PDF.`)
      const blob = await compressImage(file)
      const name = `${this.field.key}-${stamp}.jpg`
      const url = await uploadFile(this.storageFolder, name, blob)
      return { path: `${this.storageFolder}/${name}`, url, name: file.name, contentType: 'image/jpeg' }
    },
    async onPhoto (event) {
      const [file] = event.target.files
      event.target.value = ''
      if (!file) return
      await this.withUpload(async () => this.$emit('input', await this.upload(file)))
    },
    async onFiles (event) {
      const selected = Array.from(event.target.files).slice(0, (this.field.maxFiles || 30) - this.files.length)
      event.target.value = ''
      if (!selected.length) return
      await this.withUpload(async () => {
        const uploaded = []
        for (const file of selected) uploaded.push(await this.upload(file))
        this.$emit('input', [...this.files, ...uploaded])
      })
    },
    removeFile (index) { this.$emit('input', this.files.filter((_, i) => i !== index)) },
    async withUpload (task) {
      this.uploading = true
      try {
        await task()
      } catch (err) {
        console.error('Error on upload digital report file:', err)
        this.$swal({ text: err.message || 'No se pudo cargar el archivo.', buttons: false, icon: 'error', timer: 3500 })
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
$accent: #fc5b00;
$accent-ink: #a33d00;
$accent-tint: #fff1e8;
$line: #e3e5e8;
$muted: rgba(0, 0, 0, 0.6);

.report-field__label {
  font-size: 0.78rem;
  color: $muted;
  margin-bottom: 4px;
}

.report-field__empty {
  font-size: 0.8rem;
  color: $muted;
  font-style: italic;
}

// Opciones en una línea: la pregunta ocupa el espacio libre y las opciones se alinean a la derecha.
.report-field__choice {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 16px;
  min-height: 36px;
}

.report-field__choice--readonly .report-field__options {
  pointer-events: none;
}

.report-field__question {
  flex: 1 1 260px;
  font-size: 0.875rem;
  line-height: 1.35;
  color: rgba(0, 0, 0, 0.8);
}

.report-field__options {
  flex: 0 0 auto;
  flex-wrap: wrap;
  border: 1px solid $line;
  border-radius: 6px;
  max-width: 100%;

  .report-field__option.v-btn {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.7) !important;
    background-color: #fff !important;
    border-color: $line !important;
    transition: background-color 150ms ease-out, color 150ms ease-out;
  }

  .report-field__option--active.v-btn {
    background-color: $accent-tint !important;
    color: $accent-ink !important;
    font-weight: 600;
  }

  // SÍ/NO uses its own colors so it doesn't read as the same selection state as other choice fields.
  .report-field__option--yes.report-field__option--active.v-btn {
    background-color: #e8f5ec !important;
    color: #1e7a3b !important;
  }

  .report-field__option--no.report-field__option--active.v-btn {
    background-color: #fdeceb !important;
    color: #b23b2e !important;
  }
}

.report-field__photo-frame {
  height: 170px;
  border: 1px solid $line;
  border-radius: 6px;
  overflow: hidden;
  background: #fafafb;
}

.report-field__photo-empty {
  width: 100%;
  height: 100%;
  border: 0;
  background: transparent;
  cursor: pointer;
  transition: background-color 150ms ease-out;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  font-size: 0.75rem;
  color: $muted;

  &:hover:not(:disabled) {
    background: #f1f2f4;
    color: rgba(0, 0, 0, 0.75);
  }

  &:focus-visible {
    outline: 2px solid $accent;
    outline-offset: -2px;
  }
}

.report-field__total {
  font-size: 0.82rem;
  color: $muted;

  strong {
    color: rgba(0, 0, 0, 0.87);
  }
}

.report-field__files {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.report-field__file {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 2px 4px 2px 8px;
  border: 1px solid $line;
  border-radius: 6px;
  font-size: 0.8rem;
}

.report-field__file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 260px;
}

.report-field__table {
  overflow-x: auto;

  th {
    font-size: 0.72rem !important;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }
}

.report-field__table-label {
  width: 38%;
  min-width: 170px;
  font-size: 0.82rem;
  line-height: 1.3;
}

.report-field__table-cell {
  min-width: 150px;
  padding-top: 3px !important;
  padding-bottom: 3px !important;
}

.report-field__item {
  border: 1px solid $line;
  border-radius: 8px;
  background: #fafafb;
  padding: 8px 12px 10px;
  margin-bottom: 10px;
}

.report-field__item-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
}

.report-field--compact .report-field__choice {
  min-height: 0;
}
</style>
